import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

const STORAGE_KEY = "tikitly_ai_chat";

const defaultWelcomeMessage = {
  role: "assistant",
  content: "Hi! I'm your Tikitly AI assistant. Ask me anything about using the platform — like how to create events, manage tickets, handle payments, and more.",
};

function loadChatState() {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        messages: parsed.messages && parsed.messages.length > 0 ? parsed.messages : [defaultWelcomeMessage],
        isOpen: parsed.isOpen || false,
        position: parsed.position || { x: -1, y: -1 },
      };
    }
  } catch (e) {
    // ignore parse errors
  }
  return {
    messages: [defaultWelcomeMessage],
    isOpen: false,
    position: { x: -1, y: -1 },
  };
}

function saveChatState(state) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      messages: state.messages,
      isOpen: state.isOpen,
      position: state.position,
    }));
  } catch (e) {
    // ignore storage errors
  }
}

const AIChatContext = createContext(null);

export function AIChatProvider({ children }) {
  const [chatState, setChatState] = useState(loadChatState);

  // Persist to sessionStorage on every state change
  useEffect(() => {
    saveChatState(chatState);
  }, [chatState]);

  // Clear chat on logout (listen for storage key removal)
  useEffect(() => {
    const handleStorage = () => {
      const token = localStorage.getItem("userToken");
      if (!token || token === '""' || token === '') {
        setChatState({
          messages: [defaultWelcomeMessage],
          isOpen: false,
          position: { x: -1, y: -1 },
        });
        sessionStorage.removeItem(STORAGE_KEY);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const toggleChat = useCallback(() => {
    setChatState((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  const closeChat = useCallback(() => {
    setChatState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const addMessage = useCallback((message) => {
    setChatState((prev) => ({
      ...prev,
      messages: [...prev.messages, message],
    }));
  }, []);

  const setPosition = useCallback((position) => {
    setChatState((prev) => ({ ...prev, position }));
  }, []);

  const updateMessages = useCallback((updater) => {
    setChatState((prev) => ({
      ...prev,
      messages: updater(prev.messages),
    }));
  }, []);

  const clearHistory = useCallback(() => {
    setChatState((prev) => ({
      ...prev,
      messages: [defaultWelcomeMessage],
    }));
  }, []);

  return (
    <AIChatContext.Provider
      value={{
        messages: chatState.messages,
        isOpen: chatState.isOpen,
        position: chatState.position,
        toggleChat,
        closeChat,
        addMessage,
        updateMessages,
        setPosition,
        clearHistory,
      }}
    >
      {children}
    </AIChatContext.Provider>
  );
}

export function useAIChat() {
  const context = useContext(AIChatContext);
  if (!context) {
    throw new Error("useAIChat must be used within AIChatProvider");
  }
  return context;
}
